// TESSERAX — SIMULATEUR HEADLESS DÉTERMINISTE (cœur pur, testé).
//
// Reproduit le SCORE et la grille FINALE d'une run à partir de `(graine + mode + journal d'entrées)`
// — sans THREE, DOM, performance.now ni Math.random. C'est le MÊME code de transition d'état que le
// jeu vif (game/rules.ts depuis l'éclatement de main.ts) ; la VALEUR (score) vit déjà dans les
// modules purs (scoring/grid/relics/…), on n'en réimplémente QUE l'ORCHESTRATION (ordre des
// effets). Chaque fonction porte un renvoi « miroir : … » vers son homologue VIF — les anciens
// renvois de ligne main.ts:NNN pointaient dans un fichier disparu (ré-ancrés par symbole).
//
// REJEU BIT-EXACT (M3.1b) : le journal contient désormais, en plus des décisions joueur, les
// ÉVÈNEMENTS DE TIMING déterministes du moteur vif — `GRAVITY` (un cran de chute auto), `LOCK`
// (verrouillage), `ZONE_END` (fin de Zone chronométrée) — dont l'instant dépend du frame-rate (absent
// du seul journal d'entrées). Le sim est donc un PUR RELECTEUR d'évènements : il APPLIQUE le flux dans
// l'ordre, sans aucune horloge interne ni synthèse de gravité/lock → reproduction AU BIT près.

import { GRID, HEIGHT, NEXT_MULT_CAP, RELIC_CHARGE_PER_EVENT, levelForLines, MAX_CHARGE_CAP, cappedScoreMult, stackNextMult, QUEUE_LEN, MISSION_COUNT } from './constants';
import { mulberry32 } from './rng';
import { PIECES, availablePieces } from './pieces';
import { rotFns, bboxCenter } from './util';
import type { Grid, Dir } from './grid';
import { emptyGrid, boardEmpty, evaluateClears, settleDown, settleToward } from './grid';
import type { Slice } from './fold';
import { evaluateUnion } from './fold';
import { scoreClear, comboBonus, continuumBonus, softDropBonus, hardDropBonus, spinClearBonus, isHardClear, tesseraBonus, zoneBankBonus, isSpinKick, chainMult, zoneClassicMult, ZONE_CLASSIC_EXIT_COEF, perfectBonus, meterGain, basculeBonus } from './scoring';
import { linesPerCharge } from './pressure';   // seule l'économie de charge reste branchée (montée gelée puis débranchée)
import type { Relic, RelicApi, RelicEvent } from './relics';
import { relicById } from './relics';
import { activeResonanceHooks, crossedTiers, resonanceMemKey } from './resonance';
import type { ModeDef } from './modes';
import { modeDef } from './modes';
import { pickDailyMutator, buffsFor } from './mutators';
import { CODE } from './replay';
import type { InputEvent } from './replay';

// QUEUE_LEN / MISSION_COUNT : SOURCE UNIQUE dans core/constants (parité vif↔sim critique — cf. le
// commentaire là-bas). Ici on ne fait que les CONSOMMER ; plus aucune valeur locale à resynchroniser.
const ZONE_MODE: 'bank' | 'classic' = 'bank';   // le SIM cible le mode Zone par DÉFAUT (miroir : state.ZONE_MODE, défaut 'bank' — ?zone= pour l'A/B)
const OPPOSITE: Record<string, Dir> = { nx: 'px', px: 'nx', nz: 'pz', pz: 'nz' };  // miroir local (privé dans relics.ts)
// PERF-CORE-003 : tableau de wall-kicks hoisté au niveau module (évite 19 allocations par rotate()).
const KICKS = [[0,0,0],[1,0,0],[-1,0,0],[0,0,1],[0,0,-1],[2,0,0],[-2,0,0],[0,0,2],[0,0,-2],[1,0,1],[-1,0,-1],[1,0,-1],[-1,0,1],[0,-1,0],[0,1,0],[0,-2,0],[0,2,0],[0,-3,0],[0,-4,0]];
// (PARASITE_COLORS retiré avec la plomberie de montée — plus aucune case parasite possible.)
const BASC_DIR: Record<number, Dir> = {
  [CODE.BASC_PX]: 'px', [CODE.BASC_NX]: 'nx', [CODE.BASC_PZ]: 'pz', [CODE.BASC_NZ]: 'nz',
};

export interface SimPiece { name: string; color: number; cells: number[][]; }
export interface SimActive { name: string; color: number; cells: number[][]; pos: number[]; }

export interface SimState {
  // déterminisme / config
  seed: number; mode: string; def: ModeDef; meta: boolean;
  // RNG (mulberry32) — flux SÉPARÉS, comme game/flow.ts startGame (flux séparés)
  rng: () => number; relicRng: () => number;   // (riseRng retiré : jamais consommé — montée gelée puis débranchée)
  // plateau / pièces
  grid: Grid; bag: number[]; queue: SimPiece[]; current: SimActive | null; hold: SimPiece | null; holdUsed: boolean;
  // LE PLI (TOUS les modes, cf. def.fold) : tranche INACTIVE (accumulateur fantôme) + tranche active.
  // Le Pli ne consomme aucun tirage RNG → la séquence de pièces est inchangée (rejeu bit-exact préservé).
  gridV: Grid; activeSlice: Slice;
  // score / progression
  score: number; totalLines: number; slabsTotal: number; level: number; prevLevel: number;
  comboStreak: number; b2bCount: number;
  bestChain: number; runMaxB2b: number; runCross: number; runBascules: number; runPerfects: number;
  runFolds: number; runTesseras: number;
  // Compteurs de run des VOIES (cf. core/voies) : clears de CONTINUUM (b2b≥2) et lignes-équivalent
  // banquées aux sorties de Zone. Neutres au score ; tenus en miroir strict avec game/rules.ts.
  runContinuum: number; runZoneLines: number;
  maxStreak: number; maxLevel: number;
  // ressources
  meter: number; charges: number; maxCharges: number; tiltBank: number; nextClearMult: number; relicChargeBudget: number;
  // multiplicateurs de run (reliques / mutateur du jour)
  runScoreMult: number; runSpeedMult: number; runMeterMult: number; runComboMult: number;
  lockDelayMult: number; freeHold: boolean;
  // reliques
  relics: Relic[]; relicMem: Record<string, Record<string, any>>; offered: Record<number, 1>;
  spinTuck: boolean; lockSpin: boolean;
  // zone
  zoneActive: boolean; zoneClears: number; zoneGained: number; zoneBankRows: number; zoneBankSlabs: number;
  // offre de relique en cours (gèle l'application jusqu'à l'évènement RELIC/RELIC_SKIP)
  choosing: boolean; pendingOffer: boolean;
  // horloge de RELECTURE (instant du dernier évènement appliqué — pour la pose du fantôme)
  now: number; gravitySteps: number;
  // cycle de vie
  running: boolean; gameOverReason: string;
  // api relique mémoïsée + puits d'effets
  _api: RelicApi; _fx: Fx;
  // PERF-CORE-001 : cache de holeCount(S.grid) pour la durée d'un emitRelic — -1 = non calculé.
  _cachedHoles: number;
  // MESURE (BAL-DOMINANT-001) : attribution du score ADDITIF par relique. null par défaut (production /
  // anti-triche : aucun coût, aucun effet). Si une map est fournie (harness de baseline), emitRelic y
  // cumule le delta de score causé par chaque crochet `on` → on voit quel crochet additif domine.
  _scoreByRelic: Record<string, number> | null;
}

export interface SimResult {
  score: number; level: number; lines: number; grid: Grid; gridV: Grid;
  charges: number; maxCharges: number; meter: number;
  bestChain: number; maxB2b: number; cross: number; bascules: number; perfects: number;
  folds: number; tesseras: number; continuum: number; zoneLines: number;
  relics: string[]; gameOverReason: string; topOut: boolean; gravitySteps: number;
  events: { consumed: number; total: number };
}

export interface SimSnapshot {
  grid: Grid; gridV: Grid; activeSlice: Slice; current: SimActive | null;
  score: number; level: number; lines: number; charges: number; meter: number; zoneActive: boolean; now: number;
}

// Puits d'EFFETS (juice) : le moteur appelle ces points pour que main.ts rende particules/SFX/HUD.
// Le SIM headless passe NOOP_FX. Le shell (intégration M3.4, à venir) fournira `liveFx`.
export interface Fx {
  scorePopup(n: number, cells: number[][], kind?: string): void;
  onClear(res: ResolveResult): void;
  onCombo(streak: number, chain: number): void;
  onLevelUp(level: number): void;
  onRelicOffer(level: number): void;
  onZoneStart(): void;
  onZoneEnd(bankLines: number, gained: number): void;
  onBascule(payload: any): void;
  onSpawn(): void;
  onTopOut(): void;
  onLock(cells: number[][]): void;
  onFold(payload: any): void;
}
export const NOOP_FX: Fx = {
  scorePopup() {}, onClear() {}, onCombo() {}, onLevelUp() {}, onRelicOffer() {},
  onZoneStart() {}, onZoneEnd() {}, onBascule() {}, onSpawn() {}, onTopOut() {}, onLock() {}, onFold() {},
};

export interface ResolveResult { chain: number; gained: number; rows: number; slabs: number; cross: number; cells: number[][]; tesseras?: number; }

/* ======================= helpers grille / pièces ======================= */
function clone3(cells: number[][]): number[][] { return cells.map((c) => c.slice()); }
function tmplFromIndex(i: number): SimPiece { const p = PIECES[i]; return { name: p.name, color: p.color, cells: clone3(p.cells) }; }
function baseOf(name: string): SimPiece {  // miroir : game/rules.ts baseOf
  for (let i = 0; i < PIECES.length; i++) if (PIECES[i].name === name) return tmplFromIndex(i);
  return tmplFromIndex(0);
}
function absCells(p: SimActive): number[][] { return p.cells.map((c) => [c[0] + p.pos[0], c[1] + p.pos[1], c[2] + p.pos[2]]); }
function isValid(grid: Grid, cells: number[][]): boolean {  // miroir : game/util.ts isValid
  for (let i = 0; i < cells.length; i++) {
    const x = cells[i][0], y = cells[i][1], z = cells[i][2];
    if (x < 0 || x >= GRID || z < 0 || z >= GRID || y < 0 || y >= HEIGHT) return false;
    if (grid[x][y][z] !== null) return false;
  }
  return true;
}
function colHeight(grid: Grid, x: number, z: number): number { for (let y = HEIGHT - 1; y >= 0; y--) if (grid[x][y][z] !== null) return y + 1; return 0; }
function colHole(grid: Grid, x: number, z: number): boolean { let s = false; for (let y = HEIGHT - 1; y >= 0; y--) { if (grid[x][y][z] !== null) s = true; else if (s) return true; } return false; }
function maxColHeight(grid: Grid): number { let m = 0; for (let x = 0; x < GRID; x++) for (let z = 0; z < GRID; z++) { const h = colHeight(grid, x, z); if (h > m) m = h; } return m; }
function holeCount(grid: Grid): number { let n = 0; for (let x = 0; x < GRID; x++) for (let z = 0; z < GRID; z++) if (colHole(grid, x, z)) n++; return n; }
function cloneGrid(grid: Grid): Grid { return grid.map((p) => p.map((c) => c.slice())); }

/* ======================= sac / file / spawn ======================= */
function refillBag(S: SimState): void {  // miroir : game/rules.ts refillBag
  S.bag = availablePieces(S.level);
  for (let i = S.bag.length - 1; i > 0; i--) { const j = (S.rng() * (i + 1)) | 0; const t = S.bag[i]; S.bag[i] = S.bag[j]; S.bag[j] = t; }
}
function drawPiece(S: SimState): SimPiece { if (S.bag.length === 0) refillBag(S); return tmplFromIndex(S.bag.pop()!); }  // miroir : game/rules.ts drawPiece
function fillQueue(S: SimState): void { while (S.queue.length < QUEUE_LEN) S.queue.push(drawPiece(S)); }  // miroir : game/rules.ts fillQueue
function spawnPiece(S: SimState, tmpl: SimPiece, fx: Fx): void {  // miroir : game/rules.ts spawnPiece
  const cells = clone3(tmpl.cells);
  const xs = cells.map((c) => c[0]), ys = cells.map((c) => c[1]), zs = cells.map((c) => c[2]);
  const minx = Math.min.apply(0, xs), maxx = Math.max.apply(0, xs), minz = Math.min.apply(0, zs), maxz = Math.max.apply(0, zs), maxy = Math.max.apply(0, ys);
  const px = Math.floor((GRID - (maxx - minx + 1)) / 2) - minx, pz = Math.floor((GRID - (maxz - minz + 1)) / 2) - minz, py = HEIGHT - 1 - maxy;
  S.current = { name: tmpl.name, color: tmpl.color, cells, pos: [px, py, pz] };
  S.spinTuck = false;
  if (!isValid(S.grid, absCells(S.current))) {                    // miroir : game/rules.ts spawnPiece — top-out (Zen : relief d'abord)
    if (S.mode === 'zen') relief(S);
    if (!isValid(S.grid, absCells(S.current))) { S.running = false; S.gameOverReason = 'topout'; fx.onTopOut(); return; }
  }
  fx.onSpawn();
}
function relief(S: SimState): void {  // Zen : libère le sommet (≤5 étages occupés) (miroir : game/rules.ts relief)
  let removed = 0;
  for (let y = HEIGHT - 1; y >= 0 && removed < 5; y--) {
    let occ = false;
    for (let x = 0; x < GRID; x++) for (let z = 0; z < GRID; z++) { if (S.grid[x][y][z] !== null) { occ = true; S.grid[x][y][z] = null; } }
    if (occ) removed++;
  }
}
function spawn(S: SimState, fx: Fx): void {  // miroir : game/rules.ts spawn — fillQueue → shift → fillQueue → spawnPiece
  fillQueue(S); const t = S.queue.shift()!; fillQueue(S); S.holdUsed = false; spawnPiece(S, t, fx);
}
function doHold(S: SimState, fx: Fx): void {  // miroir : game/rules.ts doHold
  if (S.holdUsed || !S.current || !S.running || S.choosing || S.zoneActive) return;
  const cur = baseOf(S.current.name);
  if (S.hold) { const sw = S.hold; S.hold = cur; spawnPiece(S, sw, fx); } else { S.hold = cur; spawn(S, fx); }
  if (!S.freeHold) S.holdUsed = true;
}

/* ======================= kinématiques pièce ======================= */
function move(S: SimState, dx: number, dy: number, dz: number): boolean {  // miroir : game/rules.ts move
  const c = S.current!; const pos = [c.pos[0] + dx, c.pos[1] + dy, c.pos[2] + dz];
  const cells = c.cells.map((k) => [k[0] + pos[0], k[1] + pos[1], k[2] + pos[2]]);
  if (isValid(S.grid, cells)) { c.pos = pos; S.spinTuck = false; return true; }
  return false;
}
function rotate(S: SimState, axis: string): boolean {  // miroir : game/rules.ts rotate — rotation + repli coin + wall-kicks
  const c = S.current!; const oc = bboxCenter(c.cells);
  let nc = c.cells.map((k) => { const r = rotFns[axis]([k[0] - oc[0], k[1] - oc[1], k[2] - oc[2]]); return [r[0] + oc[0], r[1] + oc[1], r[2] + oc[2]]; });
  if (!nc.every((k) => Number.isInteger(k[0]) && Number.isInteger(k[1]) && Number.isInteger(k[2]))) {
    const r0 = c.cells.map(rotFns[axis]);
    const mn = (a: number[][]) => { const m = [1e9, 1e9, 1e9]; for (const k of a) for (let j = 0; j < 3; j++) if (k[j] < m[j]) m[j] = k[j]; return m; };
    const om = mn(c.cells), nm = mn(r0); nc = r0.map((k) => [k[0] + om[0] - nm[0], k[1] + om[1] - nm[1], k[2] + om[2] - nm[2]]);
  }
  const base = [c.pos[0], c.pos[1], c.pos[2]];
  // PERF-CORE-003 : KICKS hoisté au niveau module — pas de réallocation ici.
  for (let i = 0; i < KICKS.length; i++) {
    const k = KICKS[i], pos = [base[0] + k[0], base[1] + k[1], base[2] + k[2]];
    const abs = nc.map((q) => [q[0] + pos[0], q[1] + pos[1], q[2] + pos[2]]);
    if (isValid(S.grid, abs)) { c.cells = nc; c.pos = pos; S.spinTuck = i > 0 && isSpinKick(k[0], k[1], k[2]); return true; }   // isSpinKick : parité main.ts (rotate) — le pas de côté d'1 case n'est plus une vrille
  }
  return false;
}
function softDrop(S: SimState): void { if (move(S, 0, -1, 0)) S.score += softDropBonus(S.level); }  // miroir : game/rules.ts softDrop (lock auto via LOCK)
function hardDrop(S: SimState): void { let d = 0; while (move(S, 0, -1, 0)) d++; S.score += hardDropBonus(d, S.level); }  // miroir : game/rules.ts hardDrop (lock via LOCK)

/* ======================= clears / cascade / résolution ======================= */
function applyGravity(S: SimState): void { settleDown(S.grid); }  // miroir : game/rules.ts applyGravity

// ===== UNIF-RESOLVE-001 — RÉSOLUTION PARTAGÉE (une seule implémentation de la cascade) ======
// Le CŒUR de la cascade — effacements, gravité verticale, accumulation chaîne/score/Zone — était
// dupliqué fonction pour fonction entre les deux moteurs (contrat bit-exact de l'anti-triche).
// Ces deux fonctions sont désormais la SOURCE UNIQUE : le sim les appelle sans crochets ; le vif
// (game/rules.ts) y branche son juice via `hooks` — enregistrement des maillons pour la
// cinématique de cascade, tonalités — SANS AUCUN effet d'état : la mutation vit ici et nulle
// part ailleurs. `zoneMode` : le sim cible 'bank' (défaut produit), le vif passe state.ZONE_MODE
// (A/B ?zone=classic). Toute évolution de la cascade s'écrit UNE fois et vaut pour les deux.
export interface ResolveGridCtx { grid: Grid; level: number; runScoreMult: number; zoneActive: boolean; zoneBankRows: number; zoneBankSlabs: number; zoneClears: number; zoneGained: number; }
export interface ResolveDualCtx extends ResolveGridCtx { gridV: Grid; runTesseras: number; }
export interface ResolveHooks {
  onCleared?(link: number[][], ev: { rowCount: number; fullLayers: number[] }): void;  // maillon effacé, AVANT gravité
  onSettled?(chain: number): void;                                                      // maillon tassé, APRÈS gravité
}
export function resolveBoardOn(S: ResolveGridCtx, zoneMode: 'bank' | 'classic', hooks?: ResolveHooks): ResolveResult {
  let chain = 0, gained = 0, rows = 0, slabs = 0, cross = 0; const cells: number[][] = [];
  while (true) {
    const ev = evaluateClears(S.grid);
    if (ev.rowCount === 0 && ev.fullLayers.length === 0) break;
    chain++;
    const link: number[][] | null = hooks && hooks.onCleared ? [] : null;
    for (let i = 0; i < ev.rowCells.length; i++) { const k = ev.rowCells[i]; if (S.grid[k[0]][k[1]][k[2]] !== null) { const cell = [k[0], k[1], k[2], S.grid[k[0]][k[1]][k[2]]!]; cells.push(cell); if (link) link.push(cell); S.grid[k[0]][k[1]][k[2]] = null; } }
    for (let i = 0; i < ev.fullLayers.length; i++) { const y = ev.fullLayers[i]; for (let x = 0; x < GRID; x++) for (let z = 0; z < GRID; z++) if (S.grid[x][y][z] !== null) { const cell = [x, y, z, S.grid[x][y][z]!]; cells.push(cell); if (link) link.push(cell); S.grid[x][y][z] = null; } }
    if (link) hooks!.onCleared!(link, ev);           // le vif capture ici la pile post-effacement, pré-gravité
    settleDown(S.grid);
    if (hooks && hooks.onSettled) hooks.onSettled(chain);
    const mult = chainMult(chain);   // loi partagée (UNIF-CONST-001)
    const sc = scoreClear(ev, S.level); let g = sc.base; if (sc.isCross) cross++;
    g *= mult; g *= S.runScoreMult;
    if (S.zoneActive) {
      if (zoneMode === 'bank') { S.zoneBankRows += ev.rowCount; S.zoneBankSlabs += ev.fullLayers.length; g = 0; }
      else { const zm = zoneClassicMult(S.zoneClears); g *= zm; S.zoneClears++; S.zoneGained += g; }
    }
    gained += g; rows += ev.rowCount; slabs += ev.fullLayers.length;
  }
  return { chain, gained: Math.round(gained), rows, slabs, cross, cells };
}

// RÉSOLUTION 4D : même boucle, sur l'UNION des deux tranches (cf. core/fold.evaluateUnion). On
// efface chaque rangée/étage union-plein des DEUX cubes, on tasse chacun, on rejoue la cascade.
// Le score reprend `scoreClear` (lignes+étages+Croix) et AJOUTE le bonus Tessera pour les rangées
// ayant exigé les deux cubes. Un clear mono-tranche paie comme un clear normal.
export function resolveDualOn(S: ResolveDualCtx, zoneMode: 'bank' | 'classic', hooks?: ResolveHooks): ResolveResult {
  let chain = 0, gained = 0, rows = 0, slabs = 0, cross = 0, tesseras = 0; const cells: number[][] = [];
  const strike = (x: number, y: number, z: number) => {
    if (S.grid[x][y][z] !== null) { cells.push([x, y, z, S.grid[x][y][z]!]); S.grid[x][y][z] = null; }
    if (S.gridV[x][y][z] !== null) { cells.push([x, y, z, S.gridV[x][y][z]!]); S.gridV[x][y][z] = null; }
  };
  while (true) {
    const u = evaluateUnion(S.grid, S.gridV);
    if (u.rowCount === 0 && u.layers.length === 0) break;
    chain++;
    for (let i = 0; i < u.rows.length; i++) { const r = u.rows[i]; if (r.axis === 'x') { for (let x = 0; x < GRID; x++) strike(x, r.y, r.fixed); } else { for (let z = 0; z < GRID; z++) strike(r.fixed, r.y, z); } }
    for (let i = 0; i < u.layers.length; i++) { const y = u.layers[i]; for (let x = 0; x < GRID; x++) for (let z = 0; z < GRID; z++) strike(x, y, z); }
    settleDown(S.grid); settleDown(S.gridV);
    if (hooks && hooks.onSettled) hooks.onSettled(chain);
    const mult = chainMult(chain);   // loi partagée (UNIF-CONST-001)
    const evLike = { fullLayers: u.layers, rowCells: [], rowCount: u.rowCount, xRows: u.xRows, zRows: u.zRows };
    const sc = scoreClear(evLike, S.level); let g = sc.base; if (sc.isCross) cross++;
    if (u.tesseraRows > 0) { g += tesseraBonus(u.tesseraRows, S.level); tesseras += u.tesseraRows; }
    g *= mult; g *= S.runScoreMult;
    if (S.zoneActive) {
      if (zoneMode === 'bank') { S.zoneBankRows += u.rowCount; S.zoneBankSlabs += u.layers.length; g = 0; }
      else { const zm = zoneClassicMult(S.zoneClears); g *= zm; S.zoneClears++; S.zoneGained += g; }
    }
    gained += g; rows += u.rowCount; slabs += u.layers.length;
  }
  if (tesseras > 0) S.runTesseras += tesseras;
  return { chain, gained: Math.round(gained), rows, slabs, cross, cells, tesseras };
}

function resolveBoard(S: SimState): ResolveResult { return resolveBoardOn(S, ZONE_MODE); }  // source unique ci-dessus
function resolveDual(S: SimState): ResolveResult { return resolveDualOn(S, ZONE_MODE); }    // source unique ci-dessus

// Aiguillage de résolution / gravité selon le mode (def.fold). Les modes à Pli résolvent par UNION des
// deux tranches ; un éventuel mode SANS Pli emprunterait le chemin historique du seul puits (bit-exact).
function resolveActive(S: SimState): ResolveResult { return S.def.fold ? resolveDual(S) : resolveBoard(S); }
function gravityActive(S: SimState): void { settleDown(S.grid); if (S.def.fold) settleDown(S.gridV); }

/* ======================= reliques (RelicApi pure sur SimState) ======================= */
function meterAdd(S: SimState, n: number): void { if (!S.zoneActive) S.meter = Math.min(100, S.meter + n * S.runMeterMult); }  // miroir : game/rules.ts meterAdd
function makeRelicApi(S: SimState): RelicApi {  // miroir : game/rules.ts relicApi
  return {
    get level() { return S.level; },
    // PERF-CORE-001 : mémoïsé pour la durée d'un emitRelic (invalidé au début de chaque émission).
    get holes() { if (S._cachedHoles >= 0) return S._cachedHoles; return (S._cachedHoles = holeCount(S.grid)); },
    ev: null, self: {},
    // PAS de `rng` ici : le champ a été RETIRÉ de RelicApi (cf. relics.ts). Aucune relique ne l'a
    // jamais utilisé, et l'implémentation vive le liait au flux de PIÈCES — le premier crochet à
    // tirer aurait décalé la séquence de pièces entre joueurs et cassé le rejeu bit-exact.
    addScore(n: number) { if (n > 0) { const r = Math.round(n); S.score += r; S._fx.scorePopup(r, (this.ev && this.ev.cells) || []); } },
    addCharge(n: number) { if (n <= 0) return; const g = Math.min(n, S.relicChargeBudget); if (g <= 0) return; S.relicChargeBudget -= g; S.charges = Math.min(S.maxCharges, S.charges + g); },
    addMeter(n: number) { meterAdd(S, n); },
    extendZone() { /* fin de Zone = évènement ZONE_END enregistré → prolongation déjà captée */ },
    setNextMult(f: number) { S.nextClearMult = stackNextMult(S.nextClearMult, f); },   // loi ADDITIVE partagée — parité main.ts
    bascule(dir: string) { doBascule(S, dir as Dir, true, S._fx); },     // écho Miroir
    buffScore(f: number) { S.runScoreMult = cappedScoreMult(S.runScoreMult, f); }, buffSpeed(f: number) { S.runSpeedMult *= f; }, buffMeter(f: number) { S.runMeterMult *= f; },
    buffCombo(f: number) { S.runComboMult *= f; }, buffMaxCharge(n: number) { S.maxCharges = Math.min(MAX_CHARGE_CAP, S.maxCharges + n); S.charges = Math.min(S.maxCharges, S.charges + n); },
    buffLock(f: number) { S.lockDelayMult *= f; }, freeHold() { S.freeHold = true; },
  };
}
function emitRelic(S: SimState, event: RelicEvent, ev: any): void {  // miroir : game/rules.ts emitRelic
  S._cachedHoles = -1;  // invalide avant chaque émission : calcul unique au 1er accès
  S.relicChargeBudget = RELIC_CHARGE_PER_EVENT;
  for (let i = 0; i < S.relics.length; i++) {
    const r = S.relics[i]; const fn = r.on && r.on[event]; if (!fn) continue;
    S._api.ev = ev; S._api.self = S.relicMem[r.id] || (S.relicMem[r.id] = {});
    const before = S._scoreByRelic ? S.score : 0;
    try { fn(S._api); } catch { /* une relique ne doit jamais casser la run */ }
    if (S._scoreByRelic) S._scoreByRelic[r.id] = (S._scoreByRelic[r.id] || 0) + (S.score - before);
  }
  // RÉSONANCE DE DECK (cf. core/resonance) : les crochets de palier passent APRÈS les reliques,
  // avec la MÊME api et le MÊME budget de charge → jamais plus d'1 charge rendue par évènement,
  // toutes sources confondues. MIROIR STRICT de game/rules.emitRelic (bit-exact anti-triche).
  {
    const ids = S.relics.map((a) => a.id);
    const hooks = activeResonanceHooks(ids, event);
    for (let i = 0; i < hooks.length; i++) {
      const h = hooks[i]; const fn = h.tier.on![event]!; const key = resonanceMemKey(h.voie, h.tier.count);
      S._api.ev = ev; S._api.self = S.relicMem[key] || (S.relicMem[key] = {});
      const before = S._scoreByRelic ? S.score : 0;
      try { fn(S._api); } catch { /* une résonance ne doit jamais casser la run */ }
      if (S._scoreByRelic) S._scoreByRelic[key] = (S._scoreByRelic[key] || 0) + (S.score - before);
    }
  }
  S._api.ev = null;
}
function acquireRelic(S: SimState, r: Relic | undefined): void {  // miroir : game/rules.ts acquireRelic
  if (!r) return;
  if (r.unique !== false && S.relics.some((a) => a.id === r.id)) return;
  const beforeIds = S.relics.map((a) => a.id);
  S.relics.push(r); S.relicMem[r.id] = {};
  if (r.init) { S._api.ev = null; S._api.self = S.relicMem[r.id]; try { r.init(S._api); } catch { /* idem */ } }
  // Paliers de résonance FRANCHIS par cette acquisition → leurs init s'appliquent UNE fois,
  // dans l'ordre fixe des VOIES. MIROIR STRICT de game/rules.acquireRelic.
  const crossed = crossedTiers(beforeIds, S.relics.map((a) => a.id));
  for (let i = 0; i < crossed.length; i++) {
    const c = crossed[i]; if (!c.tier.init) continue; const key = resonanceMemKey(c.voie, c.tier.count);
    S._api.ev = null; S._api.self = S.relicMem[key] || (S.relicMem[key] = {});
    try { c.tier.init(S._api); } catch { /* idem */ }
  }
}

/* ======================= commitResolve (épine dorsale score/méta) ======================= */
function commitResolve(S: SimState, res: ResolveResult, passive: boolean, fx: Fx): void {  // miroir : game/rules.ts commitResolve
  if (res.chain > 0) {
    if (res.chain > S.bestChain) S.bestChain = res.chain;
    if (S.nextClearMult !== 1) { res.gained = Math.round(res.gained * Math.min(NEXT_MULT_CAP, S.nextClearMult)); S.nextClearMult = 1; }
    S.score += res.gained; S.slabsTotal += res.slabs;
    // (économie de survie retirée avec la montée : sans cases parasites, survivalScale valait
    // toujours 1 en résolution active, 0 en passif — simplifié tel quel. Parité game/rules.ts commitResolve.)
    if (!passive) { S.tiltBank += res.rows + res.slabs * GRID; while (S.tiltBank >= linesPerCharge(S.level)) { S.tiltBank -= linesPerCharge(S.level); S.charges = Math.min(S.maxCharges, S.charges + 1); } }
    if (!passive) meterAdd(S, meterGain(res.rows, res.slabs));
    S.totalLines += res.rows + res.slabs * GRID;
    if (!passive) emitRelic(S, 'clear', res);
    if (!passive && res.tesseras && res.tesseras > 0) emitRelic(S, 'tessera', { rows: res.tesseras, gained: res.gained });
    fx.onClear(res);
    if (!passive) {
      S.comboStreak++;
      if (S.comboStreak >= 2) { S.score += comboBonus(S.comboStreak, S.level, res.rows, res.slabs, S.runComboMult); emitRelic(S, 'combo', { streak: S.comboStreak, chain: res.chain }); fx.onCombo(S.comboStreak, res.chain); }
      if (isHardClear(res)) {
        const cb = continuumBonus(S.b2bCount, res.gained); if (cb > 0) S.score += cb;
        S.b2bCount++; if (S.b2bCount > S.runMaxB2b) S.runMaxB2b = S.b2bCount;
        if (S.b2bCount >= 2) { S.runContinuum++; emitRelic(S, 'continuum', { count: S.b2bCount, gained: res.gained }); }   // compteur de run des Voies — miroir rules.ts
      } else S.b2bCount = 0;
      if (res.cross > 0) S.runCross += res.cross;
      const cleared = S.def.fold ? (boardEmpty(S.grid) && boardEmpty(S.gridV)) : boardEmpty(S.grid);
      if (cleared) { S.score += perfectBonus(S.level); S.runPerfects++; emitRelic(S, 'perfect', {}); }
    }
  } else { S.comboStreak = 0; }
  // progression de niveau — recalcul INCONDITIONNEL ; levelup émis UNE fois par palier. SOURCE UNIQUE
  // partagée avec le moteur vif (constants.levelForLines, pilotée par LINES_PER_LEVEL) : indispensable à
  // la fidélité bit-exact du rejeu / de l'anti-triche (un divisseur figé désynchronisait sac/gravité/score).
  S.level = levelForLines(S.totalLines);
  if (S.level > S.prevLevel) {
    S.prevLevel = S.level;
    emitRelic(S, 'levelup', { level: S.level });
    fx.onLevelUp(S.level);
    if (S.meta && S.level % 2 === 0 && !S.offered[S.level]) { S.offered[S.level] = 1; openOffer(S, fx); }  // miroir : game/rules.ts commitResolve (offre aux niveaux pairs)
  }
  if (S.comboStreak > S.maxStreak) S.maxStreak = S.comboStreak;
  if (S.level > S.maxLevel) S.maxLevel = S.level;
  // Fin POSITIVE des modes courts à OBJECTIF (lignes) — miroir : game/loop.ts animate → modeComplete (le top-out reste géré au spawn).
  if (S.def.kind === 'lines' && S.totalLines >= S.def.goalLines) { S.running = false; S.gameOverReason = 'goal'; }
}

/* ======================= offre de relique (suit le chemin REPLAY) ======================= */
// Le SIM ne TIRE PAS d'offre : il pose `choosing` ; le PROCHAIN évènement RELIC/RELIC_SKIP la clôt.
function openOffer(S: SimState, fx: Fx): void { S.choosing = true; S.pendingOffer = true; fx.onRelicOffer(S.level); }
function closeOffer(S: SimState): void { S.choosing = false; S.pendingOffer = false; }  // miroir : game/flow.ts chooseDone (lastDrop → timing, non requis)

/* ======================= verrouillage (évènement LOCK) ======================= */
function lockPiece(S: SimState, fx: Fx): void {  // miroir : game/rules.ts lock + finishLock/finishLockApply fusionnés (gap = timing)
  if (!S.current) return;
  S.lockSpin = S.spinTuck; const abs = absCells(S.current);
  for (let i = 0; i < abs.length; i++) { const c = abs[i]; if (c[1] >= 0 && c[1] < HEIGHT) S.grid[c[0]][c[1]][c[2]] = S.current.color; }
  fx.onLock(abs);
  S.current = null;
  const res = resolveActive(S); commitResolve(S, res, false, fx);
  if (S.lockSpin && res.rows > 0) S.score += spinClearBonus(res.rows, S.level);  // vrille (miroir : game/rules.ts finishLockApply)
  if (S.running) spawn(S, fx);   // (un objectif atteint a pu clore la run dans commitResolve)
}

/* ======================= zone ======================= */
function tryZone(S: SimState, fx: Fx): void {  // miroir : game/rules.ts tryZone
  if (S.zoneActive || S.meter < 100 || !S.running || S.choosing) return;
  S.zoneActive = true; S.zoneClears = 0; S.zoneGained = 0; S.zoneBankRows = 0; S.zoneBankSlabs = 0; S.meter = 0;
  fx.onZoneStart();
  gravityActive(S); const res = resolveActive(S);
  if (res.chain > 0) { S.score += res.gained; S.slabsTotal += res.slabs; }
}
function endZone(S: SimState, fx: Fx): void {  // miroir : game/rules.ts endZone (déclenché par l'évènement ZONE_END)
  if (!S.zoneActive) return;
  S.zoneActive = false; let bonus = 0;
  if (ZONE_MODE === 'bank') {
    const bankLines = S.zoneBankRows + S.zoneBankSlabs * GRID;
    if (bankLines > 0) { bonus = zoneBankBonus(bankLines, S.level); S.score += bonus; S.zoneGained = bonus; }   // formule PARTAGÉE (core/scoring.zoneBankBonus) — parité main.ts (endZone) par construction
  } else {
    if (S.zoneClears > 0) { bonus = S.zoneClears * ZONE_CLASSIC_EXIT_COEF * S.level; S.score += bonus; S.zoneGained += bonus; }
  }
  S.runZoneLines += S.zoneBankRows + S.zoneBankSlabs * GRID;   // compteur de run des Voies — miroir rules.ts endZone
  emitRelic(S, 'zoneEnd', { bankLines: S.zoneBankRows + S.zoneBankSlabs * GRID });
  fx.onZoneEnd(S.zoneBankRows + S.zoneBankSlabs * GRID, S.zoneGained);
}

/* ======================= Bascule (ATOMIQUE — fidèle au score, cf. plan §3.4) ======================= */
function doBascule(S: SimState, dir: Dir, echo: boolean, fx: Fx): void {  // miroir : game/rules.ts doBascule + _bascApply
  if (!echo && (S.charges <= 0 || !S.running || S.choosing || S.zoneActive)) return;
  const holesBefore = holeCount(S.grid), hBefore = maxColHeight(S.grid);
  const moved = settleToward(S.grid, dir);   // plus d'ancres scellées (montée retirée) — parité main.ts
  let res: ResolveResult;
  if (S.def.fold) {                              // 4D : la Bascule tasse le cube ACTIF, l'union résout
    res = resolveDual(S);
  } else {
    const r1 = resolveBoard(S); applyGravity(S); const r2 = resolveBoard(S);
    res = { chain: Math.max(r1.chain, r2.chain), gained: r1.gained + r2.gained, rows: r1.rows + r2.rows, slabs: r1.slabs + r2.slabs, cross: r1.cross + r2.cross, cells: r1.cells.concat(r2.cells) };
  }
  if (!echo) { S.charges--; S.runBascules++; }
  if (S.current) { let g = 0; while (!isValid(S.grid, absCells(S.current)) && S.current.pos[1] < HEIGHT && g++ < HEIGHT) S.current.pos[1]++; }  // remontée (miroir : game/rules.ts doBascule/_bascApply)
  if (res.chain > 0) commitResolve(S, res, false, fx);          // miroir : game/rules.ts _bascApply → commitResolve
  const holesAfter = holeCount(S.grid), hAfter = maxColHeight(S.grid), repairedHoles = Math.max(0, holesBefore - holesAfter);
  const isClear = res.rows > 0 || res.slabs > 0, isCascade = res.chain >= 2, isMulti = res.rows >= 2 || res.slabs >= 1;
  const isSave = !isClear && (repairedHoles > 0 || (hBefore >= HEIGHT - 3 && hAfter < hBefore));
  const tags: string[] = []; if (isClear) tags.push('bascule_clear'); if (isMulti) tags.push('bascule_multi'); if (isCascade) tags.push('bascule_cascade'); if (repairedHoles > 0) tags.push('bascule_repair'); if (isSave) tags.push('bascule_save');
  if (!echo) {
    let bonus = basculeBonus(S.level, { multi: isMulti, cascade: isCascade, chain: res.chain, repairedHoles, save: isSave });   // loi partagée (UNIF-CONST-001)
    if (bonus > 0) { bonus = Math.round(bonus * S.runScoreMult); S.score += bonus; }
  }
  emitRelic(S, 'bascule', { dir, rows: res.rows, slabs: res.slabs, chain: res.chain, moved: moved.length, cells: res.cells, mirrored: !!echo, holesBefore, holesAfter, repairedHoles, save: isSave, tags });
  fx.onBascule({ dir, res, moved: moved.length, repairedHoles, isSave });
}

/* ======================= LE PLI (def.fold — tous les modes) ======================= */
// Échange la tranche active : le joueur empile désormais dans l'AUTRE cube, pour y combler les trous
// du premier. Coût d'une charge (mutualisé avec la Bascule). PUR côté matière : un échange ne crée
// aucun effacement (l'union est symétrique en (e,v) ; après chaque verrou/Bascule plus aucune rangée
// n'est union-pleine), donc le Pli est un coup de NAVIGATION — les Tesseras éclosent au prochain verrou.
function doFold(S: SimState, fx: Fx): void {
  if (S.charges <= 0 || !S.running || S.choosing || S.zoneActive) return;
  const tmp = S.grid; S.grid = S.gridV; S.gridV = tmp;
  S.activeSlice = S.activeSlice === 'endroit' ? 'envers' : 'endroit';
  S.charges--; S.runFolds++;
  // la pièce courante peut chevaucher la pile de la nouvelle tranche active → on la remonte (cf. Bascule).
  if (S.current) { let g = 0; while (!isValid(S.grid, absCells(S.current)) && S.current.pos[1] < HEIGHT && g++ < HEIGHT) S.current.pos[1]++; }
  emitRelic(S, 'fold', { active: S.activeSlice, folds: S.runFolds });
  fx.onFold({ active: S.activeSlice, folds: S.runFolds });
}

/* ======================= application d'un évènement du journal ======================= */
function applyEvent(S: SimState, ev: InputEvent, fx: Fx): void {
  switch (ev[1]) {
    case CODE.MOVE_XP: move(S, 1, 0, 0); break;
    case CODE.MOVE_XN: move(S, -1, 0, 0); break;
    case CODE.MOVE_ZP: move(S, 0, 0, 1); break;
    case CODE.MOVE_ZN: move(S, 0, 0, -1); break;
    case CODE.ROT_X: rotate(S, 'x'); break;
    case CODE.ROT_Y: rotate(S, 'y'); break;
    case CODE.ROT_Z: rotate(S, 'z'); break;
    case CODE.SOFT: softDrop(S); break;
    case CODE.HARD: hardDrop(S); break;       // chute ; le verrouillage suit (évènement LOCK)
    case CODE.HOLD: doHold(S, fx); break;
    case CODE.ZONE: tryZone(S, fx); break;
    case CODE.BASC_PX: case CODE.BASC_NX: case CODE.BASC_PZ: case CODE.BASC_NZ: { const d = BASC_DIR[ev[1]]; if (d) doBascule(S, d, false, fx); break; }
    case CODE.RELIC: { if (S.pendingOffer || S.choosing) { acquireRelic(S, relicById(String(ev[2]))); closeOffer(S); } break; }
    case CODE.RELIC_SKIP: { if (S.pendingOffer || S.choosing) { if (S.charges < S.maxCharges) S.charges = Math.min(S.maxCharges, S.charges + 1); closeOffer(S); } break; }
    // ---- évènements de TIMING (bit-exact) ----
    case CODE.GRAVITY: if (S.current && move(S, 0, -1, 0)) S.gravitySteps++; break;  // un cran de chute auto
    case CODE.LOCK: lockPiece(S, fx); break;                                         // verrouillage (place + résout + spawn)
    case CODE.ZONE_END: endZone(S, fx); break;                                       // fin de Zone chronométrée
    case CODE.FOLD: doFold(S, fx); break;                                            // LE PLI : échange la tranche active
    default: break;
  }
}

/* ======================= pilote (PUR RELECTEUR — aucune horloge interne) ======================= */
export interface Sim {
  readonly state: SimState;
  stepTo(tMs: number): void;
  // PILOTAGE INTERACTIF (mesure/baseline, cf. core/bot) : applique immédiatement des évènements
  // AJOUTÉS au journal, puis avance jusqu'à eux. Permet une boucle fermée « observer l'état →
  // décider → injecter » au-dessus du MÊME moteur que l'anti-triche (donc des chiffres fidèles).
  // N'altère pas le mode relecture (createSim/simulate) : ne s'utilise que si on part d'un journal vide.
  feed(events: InputEvent[]): void;
  snapshot(): SimSnapshot;
  result(): SimResult;
}

export function createSim(seed: number, mode: string, events: InputEvent[], fx: Fx = NOOP_FX): Sim {
  const S = makeInitial(seed, mode); S._fx = fx;
  let cursor = 0;
  const step = (target: number) => {
    while (cursor < events.length && events[cursor][0] <= target) { applyEvent(S, events[cursor++], fx); if (!S.running) break; }
    if (target > S.now) S.now = target;
  };
  return {
    state: S,
    stepTo(tMs: number) { step(tMs); },
    feed(evs: InputEvent[]) { for (let i = 0; i < evs.length; i++) events.push(evs[i]); step(events.length ? events[events.length - 1][0] : S.now); },
    snapshot(): SimSnapshot {
      return { grid: cloneGrid(S.grid), gridV: cloneGrid(S.gridV), activeSlice: S.activeSlice, current: S.current ? { name: S.current.name, color: S.current.color, cells: clone3(S.current.cells), pos: S.current.pos.slice() } : null,
        score: S.score, level: S.level, lines: S.totalLines, charges: S.charges, meter: S.meter, zoneActive: S.zoneActive, now: S.now };
    },
    result(): SimResult { return resultOf(S, events.length, cursor); },
  };
}

// One-shot : applique TOUT le journal dans l'ordre (la fin de run vient du journal : LOCK final →
// spawn impossible → top-out ; ou objectif lignes ; modes chronométrés → épuisement du journal).
export function simulate(seed: number, mode: string, events: InputEvent[], fx: Fx = NOOP_FX): SimResult {
  const S = makeInitial(seed, mode); S._fx = fx;
  let cursor = 0;
  for (; cursor < events.length && S.running; cursor++) applyEvent(S, events[cursor], fx);
  if (S.running && S.def.kind === 'timed') { S.running = false; S.gameOverReason = 'time'; }  // fin au chrono (hors journal)
  if (events.length) S.now = events[events.length - 1][0];
  return resultOf(S, events.length, cursor);
}

/* ======================= init ======================= */
function makeInitial(seed: number, mode: string): SimState {
  const def = modeDef(mode);
  const S: SimState = {
    seed: seed >>> 0, mode, def, meta: def.meta,
    rng: mulberry32(seed >>> 0), relicRng: mulberry32((seed ^ 0x1b873593) >>> 0),
    grid: emptyGrid(), gridV: emptyGrid(), activeSlice: 'endroit',
    bag: [], queue: [], current: null, hold: null, holdUsed: false,
    score: 0, totalLines: 0, slabsTotal: 0, level: 1, prevLevel: 1, comboStreak: 0, b2bCount: 0,
    bestChain: 0, runMaxB2b: 0, runCross: 0, runBascules: 0, runPerfects: 0, maxStreak: 0, maxLevel: 1,
    runFolds: 0, runTesseras: 0, runContinuum: 0, runZoneLines: 0,
    meter: 0, charges: 0, maxCharges: 3, tiltBank: 0, nextClearMult: 1, relicChargeBudget: RELIC_CHARGE_PER_EVENT,
    runScoreMult: 1, runSpeedMult: 1, runMeterMult: 1, runComboMult: 1, lockDelayMult: 1, freeHold: false,
    relics: [], relicMem: {}, offered: {}, spinTuck: false, lockSpin: false,
    zoneActive: false, zoneClears: 0, zoneGained: 0, zoneBankRows: 0, zoneBankSlabs: 0,
    choosing: false, pendingOffer: false, now: 0, gravitySteps: 0,
    running: true, gameOverReason: '',
    _api: null as any, _fx: NOOP_FX,
    _cachedHoles: -1,
    _scoreByRelic: null,
  };
  S._api = makeRelicApi(S);
  if (mode === 'daily') {   // mutateur du jour (défi seul) — miroir : game/flow.ts startGame (mutateur du jour)
    const b = buffsFor(pickDailyMutator(seed >>> 0));
    S.runScoreMult = cappedScoreMult(S.runScoreMult, b.scoreMult); S.runSpeedMult *= b.speedMult; S.runMeterMult *= b.meterMult; S.runComboMult *= b.comboMult;
    S.maxCharges = Math.min(MAX_CHARGE_CAP, S.maxCharges + b.maxChargesAdd); if (b.freeHold) S.freeHold = true; S.lockDelayMult *= b.lockMult;
    S.charges = Math.min(S.maxCharges, S.charges + b.startCharges);
  }
  // ONBOARDING (BAL-FIRST/BASCULE-START-001) : 1 charge offerte au départ des modes endless non seedés
  // (libre/zen). IDENTIQUE au moteur vif (main.ts startGame) → re-simulation bit-exacte préservée.
  if (mode === 'free' || mode === 'zen') S.charges = Math.max(S.charges, 1);
  // ORDRE RNG CRITIQUE (miroir : game/flow.ts startGame : pickMissions → fillQueue → spawn) : pickMissions (5 tirages, meta seul) → fillQueue → spawn.
  if (S.meta) for (let i = MISSION_COUNT - 1; i > 0; i--) (S.rng() * (i + 1)) | 0;
  fillQueue(S);
  spawn(S, S._fx);
  return S;
}

function resultOf(S: SimState, total: number, consumed: number): SimResult {
  return {
    score: S.score, level: S.level, lines: S.totalLines, grid: S.grid, gridV: S.gridV,
    charges: S.charges, maxCharges: S.maxCharges, meter: S.meter,
    bestChain: S.bestChain, maxB2b: S.runMaxB2b, cross: S.runCross, bascules: S.runBascules, perfects: S.runPerfects,
    folds: S.runFolds, tesseras: S.runTesseras, continuum: S.runContinuum, zoneLines: S.runZoneLines,
    relics: S.relics.map((r) => r.id), gameOverReason: S.gameOverReason, topOut: S.gameOverReason === 'topout', gravitySteps: S.gravitySteps,
    events: { consumed, total },
  };
}

// Hash compact de la grille finale (placement) — pour le test de fidélité (gridHash).
export function hashGrid(grid: Grid): string {
  let h = 2166136261 >>> 0;
  for (let x = 0; x < GRID; x++) for (let y = 0; y < HEIGHT; y++) for (let z = 0; z < GRID; z++) {
    const v = grid[x][y][z]; const n = v === null ? 0 : (v >>> 0) + 1;
    h ^= n; h = Math.imul(h, 16777619) >>> 0;
  }
  return h.toString(16);
}

// Hash des DEUX tranches (def.fold — tous modes) — pour le test de fidélité 4D (rejeu bit-exact des deux cubes).
export function hashDual(grid: Grid, gridV: Grid): string { return hashGrid(grid) + ':' + hashGrid(gridV); }
